import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
} from '@mui/material';
import React, { useCallback } from 'react';

import { AbgTypography, CloseIcon } from '@abg/kit-base';
import { RegularModalProps } from '@abg/typings/web/global-modal-provider.types';

import { useAnalyticsGeneralEngagement } from '../../shared/analytics/hooks/use-analytics-general-engagement';
import { sanitizeHtml } from '../../shared/utils';

import { dialogStyling } from './ConcurConnectDialog.styling';
import { ConcurConnectDialogProps } from './types';
import { getDialogContent } from './utils';

export const ConcurConnectDialog: React.FC<
  RegularModalProps<never> & ConcurConnectDialogProps
> = ({ view, companyName, i18n, onAgree, onClose, onDecline, resetView }) => {
  const { emitEvent: emitGeneralEngagementEvent } =
    useAnalyticsGeneralEngagement();

  const { title, description } = getDialogContent({
    view,
    i18n,
    companyName,
  });

  const handleClose = useCallback(() => {
    resetView?.();
    onClose();
  }, [resetView, onClose]);

  const handleClick = (event: React.MouseEvent, handler: () => void) => {
    emitGeneralEngagementEvent(event.currentTarget);
    handler();
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      sx={dialogStyling.wrapper}
      data-testid="concur-connect-dialog"
    >
      <DialogTitle sx={dialogStyling.title}>
        {title}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={dialogStyling.closeWrapper}
        >
          <CloseIcon sx={dialogStyling.closeIcon} />
        </IconButton>
      </DialogTitle>
      <Divider sx={dialogStyling.divider} />
      <DialogContent
        sx={dialogStyling.content}
        data-testid="concur-connect-dialog-content"
      >
        <AbgTypography
          variant="body1"
          component="div"
          sx={dialogStyling.description}
          data-testid="concur-connect-dialog-description"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(description || ''),
          }}
        />
      </DialogContent>

      {view?.isStart && (
        <>
          <DialogActions sx={dialogStyling.actions}>
            <Button
              sx={dialogStyling.button}
              className="secondary"
              onClick={(event) => handleClick(event, onDecline)}
            >
              {i18n['global.noIAmNot.label']}
            </Button>
            <Button
              sx={dialogStyling.button}
              className="primary"
              onClick={(event) => handleClick(event, onAgree)}
            >
              {i18n['global.yesIAm.label']}
            </Button>
          </DialogActions>

          <AbgTypography
            variant="metadataRegular"
            component="div"
            sx={dialogStyling.disclaimer}
          >
            {i18n['loginModal.confirmEmployment.text']}
          </AbgTypography>
        </>
      )}
    </Dialog>
  );
};
